import {
  isConcludedSale,
  refundedAmountOf,
  SaleRecord,
  SALES_GATEWAYS,
  SalesGateway,
} from "./SaleRecord.ts";
import {
  bucketIndexOf,
  bucketsOf,
  isWithin,
  SalesBucket,
  SalesGranularity,
  SalesPeriod,
} from "./salesPeriod.ts";
import {
  CATEGORICAL_SERIES_COLORS,
  OTHER_SERIES_COLOR,
  SALES_GATEWAY_COLORS,
  SALES_GATEWAY_LABELS,
} from "./salesLabels.ts";

export enum SalesTrendMetric {
  Sold = "sold",
  NetSold = "net",
  Collected = "collected",
  Orders = "orders",
}

export const SALES_TREND_METRICS = Object.values(SalesTrendMetric);

export enum SalesTrendSplit {
  Gateway = "gateway",
  PaymentMethod = "method",
  Item = "item",
  Installments = "installments",
}

export const SALES_TREND_SPLITS = Object.values(SalesTrendSplit);

export const OTHER_SERIES_KEY = "other";

export type SalesTrendSeries = { key: string; label: string; color: string };

export type SalesTrendBucket = SalesBucket & {
  valuesBySeries: Record<string, number>;
  total: number;
};

export type SalesTrend = {
  series: SalesTrendSeries[];
  buckets: SalesTrendBucket[];
};

type DatedValue = { dateUtc: string; value: number };

function datedValuesOf(
  record: SaleRecord,
  metric: SalesTrendMetric,
): DatedValue[] {
  if (metric === SalesTrendMetric.Collected)
    return record.payments.map((payment) => ({
      dateUtc: payment.paidAtUtc,
      value: payment.amountInCents,
    }));
  if (!isConcludedSale(record)) return [];
  return [{ dateUtc: record.orderedAtUtc, value: saleValueOf(record, metric) }];
}

function saleValueOf(record: SaleRecord, metric: SalesTrendMetric): number {
  if (metric === SalesTrendMetric.Orders) return 1;
  if (metric === SalesTrendMetric.NetSold)
    return record.amountDueInCents - refundedAmountOf(record);
  return record.amountDueInCents;
}

function valueWithin(
  record: SaleRecord,
  metric: SalesTrendMetric,
  period: SalesPeriod,
): number {
  return datedValuesOf(record, metric)
    .filter(({ dateUtc }) => isWithin(dateUtc, period))
    .reduce((total, { value }) => total + value, 0);
}

function gatewaySeries(): SalesTrendSeries[] {
  return SALES_GATEWAYS.map((gateway: SalesGateway) => ({
    key: gateway,
    label: SALES_GATEWAY_LABELS[gateway],
    color: SALES_GATEWAY_COLORS[gateway],
  }));
}

function largestSeriesFirst({
  records,
  metric,
  period,
  seriesKeyOf,
  seriesLabelOf,
}: {
  records: SaleRecord[];
  metric: SalesTrendMetric;
  period: SalesPeriod;
  seriesKeyOf: (record: SaleRecord) => string;
  seriesLabelOf: (key: string) => string;
}): SalesTrendSeries[] {
  const totalsByKey = new Map<string, number>();
  for (const record of records) {
    const value = valueWithin(record, metric, period);
    if (value === 0) continue;
    const key = seriesKeyOf(record);
    totalsByKey.set(key, (totalsByKey.get(key) ?? 0) + value);
  }
  const keysBySize = [...totalsByKey.entries()]
    .sort(([, first], [, second]) => second - first)
    .map(([key]) => key);
  const namedSeries = keysBySize
    .slice(0, CATEGORICAL_SERIES_COLORS.length)
    .map((key, slot) => ({
      key,
      label: seriesLabelOf(key),
      color: CATEGORICAL_SERIES_COLORS[slot],
    }));
  return keysBySize.length > CATEGORICAL_SERIES_COLORS.length
    ? [
        ...namedSeries,
        { key: OTHER_SERIES_KEY, label: "Altri", color: OTHER_SERIES_COLOR },
      ]
    : namedSeries;
}

export function salesTrendSeriesOf({
  split,
  recordsOfThePeriod,
  metric,
  period,
  seriesKeyOf,
  seriesLabelOf,
}: {
  split: SalesTrendSplit;
  recordsOfThePeriod: SaleRecord[];
  metric: SalesTrendMetric;
  period: SalesPeriod;
  seriesKeyOf: (record: SaleRecord) => string;
  seriesLabelOf: (key: string) => string;
}): SalesTrendSeries[] {
  return split === SalesTrendSplit.Gateway
    ? gatewaySeries()
    : largestSeriesFirst({
        records: recordsOfThePeriod,
        metric,
        period,
        seriesKeyOf,
        seriesLabelOf,
      });
}

export function buildSalesTrend({
  records,
  series,
  seriesKeyOf,
  metric,
  period,
  granularity,
}: {
  records: SaleRecord[];
  series: SalesTrendSeries[];
  seriesKeyOf: (record: SaleRecord) => string;
  metric: SalesTrendMetric;
  period: SalesPeriod;
  granularity: SalesGranularity;
}): SalesTrend {
  const knownSeriesKeys = new Set(series.map((each) => each.key));
  const buckets: SalesTrendBucket[] = bucketsOf(period, granularity).map(
    (bucket) => ({ ...bucket, valuesBySeries: {}, total: 0 }),
  );
  for (const record of records) {
    const recordKey = seriesKeyOf(record);
    const seriesKey = knownSeriesKeys.has(recordKey)
      ? recordKey
      : OTHER_SERIES_KEY;
    for (const { dateUtc, value } of datedValuesOf(record, metric)) {
      const bucket = buckets[bucketIndexOf(dateUtc, buckets)];
      if (!bucket) continue;
      bucket.valuesBySeries[seriesKey] =
        (bucket.valuesBySeries[seriesKey] ?? 0) + value;
      bucket.total += value;
    }
  }
  const seriesWithValues = series.filter((each) =>
    buckets.some((bucket) => (bucket.valuesBySeries[each.key] ?? 0) > 0),
  );
  return { series: seriesWithValues, buckets };
}
